import React, { createContext, useContext, useReducer } from "react";
import { FileModel } from "@admin/domain/files/model/FileModel";
import { ProductMediaModel } from "@admin/domain/products/models/edit/ProductAttributesModel";
import { v4 as uuid } from "uuid";

export enum ActionType {
    DeleteFile = `delete_file`,
    DeleteMobileAppFile = `delete_mobile_app_file`,
    AddFile = `add_file`,
    AddMobileAppFile = `add_mobile_app_file`,
    Sort = `sort`,
}

type ProductMediaDispatchContextProps = {
    type: ActionType;
    mediaId?: number | string;
    productMediaFile?: FileModel;
    newMedia?: ProductMediaModel[];
};

const FilesContext = createContext<ProductMediaModel[]>([]);
const ProductMediaDispatchContext = createContext<React.Dispatch<ProductMediaDispatchContextProps>>(undefined as any);

type ProductMediaProviderProps = {
    filesInit: ProductMediaModel[];
    children: React.ReactNode;
};

export function ProductMediaFilesProvider({ filesInit, children }: ProductMediaProviderProps) {
    const [productMedia, dispatch] = useReducer<React.Reducer<ProductMediaModel[], ProductMediaDispatchContextProps>>(
        productMediaFilesReducer,
        filesInit,
    );

    return (
        <FilesContext.Provider value={productMedia}>
            <ProductMediaDispatchContext.Provider value={dispatch}>{children}</ProductMediaDispatchContext.Provider>
        </FilesContext.Provider>
    );
}

function productMediaFilesReducer(
    productMedia: ProductMediaModel[],
    action: ProductMediaDispatchContextProps,
): ProductMediaModel[] {
    switch (action.type) {
        case ActionType.AddFile: {
            return [...productMedia, { id: uuid(), file: action.productMediaFile as FileModel, mobile_app_file: null }];
        }
        case ActionType.AddMobileAppFile: {
            return productMedia.map((media) => {
                if (media.id === action.mediaId) {
                    return {
                        ...media,
                        mobile_app_file: action.productMediaFile as FileModel,
                    };
                }

                return media;
            });
        }
        case ActionType.DeleteFile: {
            return productMedia.filter((media) => media.id !== action.mediaId);
        }
        case ActionType.DeleteMobileAppFile: {
            return productMedia.map((media) => {
                if (media.id === action.mediaId) {
                    return {
                        ...media,
                        mobile_app_file: null,
                    };
                }

                return media;
            });
        }
        case ActionType.Sort: {
            return [...(action.newMedia || [])];
        }
        default: {
            throw Error(`Unknown action: ${action.type}`);
        }
    }
}

export function useProductMedia() {
    return useContext(FilesContext);
}

export function useProductMediaDispatch() {
    return useContext(ProductMediaDispatchContext);
}
